import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import jwt_decode from "jwt-decode";
import cookies from "js-cookie";
import repo from "@/lib/repository";
import {
  CalendarEntryDto,
  GrandidResponse,
  ServerError,
  TokenRequest,
  TokenResponse,
  UserDto,
  UserSession,
} from "@/lib/types"
import { validateServerError } from "@/lib/utils";
import { HttpStatus } from '@/lib/enums/http-status.enum'

import { AppPaths } from '@/app-constants'


export const requestOTP = createAsyncThunk<
  { success: true },
  TokenRequest,
  { rejectValue: ServerError }
>("profile/request/otp", async (form, { rejectWithValue }) => {
  try {
    await repo.post("/specialists/request-otp", form, { headers: { "content-type": "application/json" } });
    return { success: true };
  } catch (error: any) {
    return rejectWithValue(validateServerError(error));
  }
});

export const verifyOTP = createAsyncThunk<
  UserSession,
  TokenRequest,
  { rejectValue: ServerError }
>("profile/verify/otp", async (form, { rejectWithValue }) => {
  try {
    const response = await repo.post<TokenResponse, AxiosResponse<TokenResponse, TokenRequest>>("/specialists/verify-otp", form, { headers: { "content-type": "application/json" } });
    cookies.set("access_token", response.data.access_token, { path: "/" });
    return jwt_decode<UserSession>(response.data.access_token);
  } catch (error: any) {
    return rejectWithValue(validateServerError(error));
  }
});

export const getSignIn = createAsyncThunk("profile/get/sign-in", async (form: TokenRequest, { rejectWithValue }) => {
  try {
    const response = await repo.post<TokenResponse, AxiosResponse<TokenResponse, TokenRequest>, TokenRequest>("/users/sign-in", form);
    cookies.set("access_token", response.data.access_token, { path: "/" });
    return jwt_decode<UserSession>(response.data.access_token);
  }
  catch (error: any | AxiosError) {
    return rejectWithValue(validateServerError(error));
  }
});

export const getGrandidRef = createAsyncThunk<GrandidResponse, void>("profile/get/grandid/sessionid", async (_, { rejectWithValue }) => {
  try {
    const response = await repo.get<GrandidResponse>("/third-party/grand-id/session/specialists");
    return response.data;
  }
  catch (error: any | AxiosError) {
    return rejectWithValue(validateServerError(error));
  }
});

export const getSignInGrandid = createAsyncThunk<UserSession, { sessionId: string }>("profile/get/sign-in/grandid", async (form, { rejectWithValue }) => {
  try {
    const response = await repo
      .post<TokenResponse, AxiosResponse<TokenResponse, { sessionId: string }>, { sessionId: string }>("/third-party/grand-id/sign-in/specialists", form);
    cookies.set("access_token", response.data.access_token, { path: "/" });
    return jwt_decode<UserSession>(response.data.access_token);
  }
  catch (error: any | AxiosError) {
    return rejectWithValue(validateServerError(error));
  }
});

export const getTodoList = createAsyncThunk("profile/get/todo/list", async (_, { rejectWithValue }) => {
  try {
    const response = await repo.get<CalendarEntryDto[]>("/specialists/calendar-entries/list");
    return response.data;
  }
  catch (error: any | AxiosError) {
    return rejectWithValue(validateServerError(error));
  }
});

export const getSignOut = createAsyncThunk("profile/get/sign-out", async (_, { rejectWithValue }) => {
  try {
    await repo.post("/users/sign-out");
  } catch (error: any | AxiosError) {
    return rejectWithValue(validateServerError(error));
  } finally {
    cookies.remove("access_token", { path: "/" });
    window.location.href = new URL(AppPaths.SIGN_IN, window.location.origin).toString()
  }
});

export const getProfile = createAsyncThunk<UserDto>("profile/get/profile", async (_, { rejectWithValue, dispatch }) => {
  try {
    const response = await repo.get<UserDto>("/specialists/profile");
    return response.data;
  } catch (error: any | AxiosError) {
    if(error?.response.status === HttpStatus.NOT_FOUND) {
      dispatch(getSignOut())
    }

    return rejectWithValue(validateServerError(error));
  }
});
